import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const LINES = [
    { text: "A village.", accent: false },
    { text: "A state away.", accent: false },
    { text: "A way back.", accent: true },
];

const lineVariants = {
    hidden: { y: "115%" },
    show: (i) => ({
        y: "0%",
        transition: {
            duration: 1.15,
            ease: [0.16, 1, 0.3, 1],
            delay: 0.25 + i * 0.14,
        },
    }),
};

const fade = (delay) => ({
    hidden: { opacity: 0, y: 18 },
    show: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay },
    },
});

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
    const y = useTransform(scrollYProgress, [0, 1], [0, -90]);

    return (
        <section
            id="top"
            ref={ref}
            className="relative flex min-h-[100svh] flex-col justify-center overflow-hidden px-6 pb-24 pt-32 lg:px-14"
        >
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-cream/95 via-cream/75 to-cream/30 sm:via-cream/45 sm:to-transparent" />

            <motion.div style={{ opacity, y }} className="relative max-w-4xl">
                <motion.p
                    variants={fade(0.1)}
                    initial="hidden"
                    animate="show"
                    className="mb-6 font-mono text-[11px] uppercase tracking-[0.3em] text-terracotta"
                >
                    Roots &amp; Routes — my unbroken journey
                </motion.p>

                <h1 className="font-display text-[clamp(2.9rem,9vw,5.75rem)] font-semibold leading-[1.02] tracking-tight text-ink">
                    {LINES.map((line, i) => (
                        <span key={line.text} className="block overflow-hidden pb-1">
                            <motion.span
                                custom={i}
                                variants={lineVariants}
                                initial="hidden"
                                animate="show"
                                className={`block ${
                                    line.accent
                                        ? "italic text-terracotta"
                                        : "text-ink"
                                }`}
                            >
                                {line.text}
                            </motion.span>
                        </span>
                    ))}
                </h1>

                <motion.p
                    variants={fade(0.9)}
                    initial="hidden"
                    animate="show"
                    className="mt-7 max-w-md text-base leading-relaxed text-inksoft sm:text-lg"
                >
                    <span className="font-semibold text-ink">
                        Arpit Shukla · Developer
                    </span>{" "}
                    — a small village in UP, a college in Bengaluru, and a way
                    back. Press play and listen; the story tells itself.
                </motion.p>
            </motion.div>

            <motion.div
                variants={fade(1.3)}
                initial="hidden"
                animate="show"
                data-testid="hero-scroll-cue"
                className="absolute bottom-8 left-6 flex items-center gap-4 lg:left-14"
            >
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-inksoft">
                    Scroll
                </span>
                <span className="relative h-10 w-px bg-line">
                    <span className="cue-line absolute inset-0 bg-terracotta" />
                </span>
            </motion.div>
        </section>
    );
};

export default Hero;
